import axios, { type AxiosInstance, type AxiosError } from 'axios'
import { toastManager } from '@/composables/useToast'
import type { ProviderCredentialsResponse } from './types'

export interface SuggestionItem {
  id: string
  text: string
  source: string
  user_id: string
  type: string
  category: string
  status: 'pending' | 'reviewed' | 'rejected'
  created_at: string
  reviewed_at?: string | null
  admin_comment?: string | null
}

export interface FaqEntry {
  id: number
  question: string
  answer: string
  category: string | null
  variants?: string[]
  min_role: 'user' | 'manager' | 'admin'
  is_active: boolean
  hit_count: number
  helpful_count: number
  not_helpful_count: number
  department?: string | null
  access_level?: string | null
}

export interface FaqTopItem {
  question: string
  count: number
  answer: string
}

export interface FaqTopParams {
  page?: number
  per_page?: number
  search?: string
  days?: number
}

export interface FaqTopResponse {
  items: FaqTopItem[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

export interface Announcement {
  id: string
  title: string
  body: string
  created_at: string | null
  audience: {
    roles: string[]
    departments: string[]
  }
}

export interface AnnouncementsResponse {
  items: Announcement[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

export interface CreateAnnouncementPayload {
  title: string
  body: string
  roles?: string[]
  departments?: string[]
}

export interface CreateAnnouncementResponse {
  success: boolean
  id: string
  tg_delivered: number
  tg_failed: number
  fcm_delivered: number
  fcm_failed: number
  fcm_no_devices: number
  total: number
}

// Зеркало interfaces/web/routers/announcements.py:
// TITLE_MAX_LEN=200, BODY_MAX_LEN=20000, DEPARTMENTS_MAX_COUNT=20,
// DEPARTMENT_MAX_LEN=100, VALID_ROLES=(admin, manager, user), 'general' запрещён.
export const ANNOUNCEMENT_TITLE_MAX = 200
export const ANNOUNCEMENT_BODY_MAX = 20000
export const ANNOUNCEMENT_DEPARTMENTS_MAX = 20
export const ANNOUNCEMENT_DEPARTMENT_MAX_LEN = 100
export const ANNOUNCEMENT_ROLES = ['admin', 'manager', 'user'] as const

class ApiService {
  private client: AxiosInstance
  private token: string | null = null
  private userEmail: string | null = null

  constructor() {
    this.client = axios.create({
      baseURL: '/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Load token and user from localStorage
    this.token = localStorage.getItem('token')
    if (this.token) {
      this.setAuthHeader(this.token)
    }
    
    // Load user email for identification
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser)
        this.userEmail = user?.email || null
      } catch {
        this.userEmail = null
      }
    }

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth header if token exists
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`
        }
        // Add user email header for identification
        if (this.userEmail) {
          config.headers['X-User-Email'] = this.userEmail
        }
        // Add user ID header (from localStorage user object)
        const user = localStorage.getItem('user')
        if (user) {
          try {
            const userData = JSON.parse(user)
            if (userData.user_id) {
              config.headers['X-User-ID'] = String(userData.user_id)
            }
          } catch {
            // ignore parse error
          }
        }
        return config
      },
      (error) => Promise.reject(error)
    )

  // Response interceptor — уважает X-Silent-Error (для polling/фоновых запросов без тоста)
  this.client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      // Вход/сброс пароля: 401 здесь — неверный пароль/код, а не мёртвая
      // сессия. Редирект стирал бы текст ошибки — пропускаем.
      const url = error.config?.url || ''
      const isAuthCall = /\/users\/(login|reset-request|reset-confirm)(\?|$)/.test(url)
      if (error.response?.status === 401 && !isAuthCall) {
        // Token expired or invalid
        this.clearAuth()
        window.location.href = '/login'
      }
      // п.4: мёртвая сессия приходит как 403 «Сессия недействительна...»
      // (resolve_auth_user), а не 401. Без этого пользователь бесконечно
      // получает 403 на все /api/admin/* с тем же токеном — разрываем
      // цикл так же, как при 401: чистим auth и уходим на /login.
      if (error.response?.status === 403) {
        const data = error.response.data as { error?: string; message?: string } | undefined
        const msg = `${data?.error || ''} ${data?.message || ''}`
        if (msg.includes('Сессия недействительна')) {
          this.clearAuth()
          window.location.href = '/login'
        }
      }
      const silentHeader = (error.config?.headers as Record<string, unknown>)?.['X-Silent-Error']
      const isSilent = silentHeader === 'true' || silentHeader === true
      if (isSilent) {
        // Не показываем toast — вызывающий код сам обработает ошибку
        const data = error.response?.data as { error?: string; message?: string; error_code?: string; retry_after?: number } | undefined
        const msg = data?.error || data?.message || (error.request ? 'Ошибка сети. Проверьте подключение.' : (error.message || 'Неизвестная ошибка'))
        const err = new Error(String(msg)) as Error & { errorCode?: string; status?: number; retryAfter?: number }
        if (data?.error_code) err.errorCode = data.error_code
        if (error.response?.status) err.status = error.response.status
        if (typeof data?.retry_after === 'number') err.retryAfter = data.retry_after
        return Promise.reject(err)
      }
      return Promise.reject(this.handleError(error))
    }
  )
  }

  private setAuthHeader(token: string) {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  private clearAuth() {
    this.token = null
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    delete this.client.defaults.headers.common['Authorization']
  }

  private handleError(error: AxiosError): Error {
    if (error.response) {
      // Server responded with error
      const data = error.response.data as { error?: string; message?: string; error_code?: string; retry_after?: number }
      const message = data?.error || data?.message || `HTTP ${error.response.status}`
      const err = new Error(message) as Error & { errorCode?: string; status?: number; retryAfter?: number }
      err.status = error.response.status
      if (data?.error_code) err.errorCode = data.error_code
      if (typeof data?.retry_after === 'number') err.retryAfter = data.retry_after
      toastManager.error(message)
      return err
    } else if (error.request) {
      // Ответа нет. Причин две, и «проверьте подключение» не помогает ни в
      // одной: либо сеть действительно оборвалась, либо сервер не успел
      // ответить — а он бывает занят распознаванием скана (22 с на страницу,
      // до 2.5 часов на документ). Владелец жаловался именно на второй случай.
      const msg = 'Сервер не ответил вовремя. Если идёт распознавание документов — это ожидаемо, смотрите вкладку «Ресурсы». Иначе проверьте подключение.'
      toastManager.error(msg)
      return new Error(msg)
    } else {
      // Request setup error
      const msg = error.message || 'Неизвестная ошибка'
      toastManager.error(msg)
      return new Error(msg)
    }
  }

  setToken(token: string | null) {
    this.token = token
    if (token) {
      localStorage.setItem('token', token)
      this.setAuthHeader(token)
    } else {
      this.clearAuth()
    }
  }

  // Auth endpoints (X-Silent-Error: страницу входа ошибку показывает inline)
  async login(userId: number, password?: string) {
    const payload: Record<string, unknown> = { user_id: userId }
    if (password) payload.password = password
    const { data } = await this.client.post('/users/login', payload, {
      headers: { 'X-Silent-Error': 'true' },
    })
    if (data.success && data.token) {
      this.setToken(data.token)
      localStorage.setItem('user', JSON.stringify(data.user))
    }
    return data
  }

  async loginByEmail(email: string, password?: string) {
    const payload: Record<string, unknown> = { email }
    if (password) payload.password = password
    const { data } = await this.client.post('/users/login', payload, {
      headers: { 'X-Silent-Error': 'true' },
    })
    if (data.success && data.token) {
      this.setToken(data.token)
      localStorage.setItem('user', JSON.stringify(data.user))
      // Store email for API identification
      if (data.user?.email) {
        this.userEmail = data.user.email
      }
    }
    return data
  }

  async logout() {
    try {
      await this.client.post('/users/logout')
    } finally {
      this.setToken(null)
      localStorage.removeItem('user')
    }
  }

  async requestPasswordReset(email: string) {
    const { data } = await this.client.post('/users/reset-request', { email })
    if (data.error) throw new Error(data.error)
    return data
  }

  async confirmPasswordReset(email: string, code: string, new_password: string) {
    const { data } = await this.client.post('/users/reset-confirm', { email, code, new_password })
    if (data.error) throw new Error(data.error)
    return data
  }

  async getCurrentUser() {
    const { data } = await this.client.get('/users/me')
    return data.user
  }

  async updateProfile(updates: { name?: string; department?: string; departments?: string[]; current_password?: string; new_password?: string }) {
    const { data } = await this.client.put('/users/me', updates)
    if (data.error) throw new Error(data.error)
    return data.user
  }

  // Справочник отделов для чтения (п.3): доступен всем залогиненным,
  // без admin — иначе не-админ получает 403-спам в Профиле.
  async getUserDepartments(): Promise<string[]> {
    const { data } = await this.client.get('/users/departments')
    return (data?.departments as string[]) || []
  }

  // Chat endpoints
  async askQuestion(question: string) {
    const { data } = await this.client.post('/ask', { question })
    return data
  }

  async uploadMedia(file: File, caption: string = ''): Promise<{ success: boolean; type: string; result: string }> {
    const formData = new FormData()
    formData.append('file', file)
    if (caption) {
      formData.append('caption', caption)
    }
    
    const { data } = await this.client.post('/chat/media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return data
  }

  // Admin endpoints
  async getAdminStats() {
    const { data } = await this.client.get('/admin/stats')
    return data
  }

  async getUsers(page = 1, perPage = 20, params?: { search?: string; role?: string; department?: string }) {
    const { data } = await this.client.get('/admin/users', { params: { page, per_page: perPage, ...params } })
    return data
  }

  async setUserRole(userId: number | string, role: string) {
    const { data } = await this.client.post(`/admin/users/${userId}/role`, { role })
    return data
  }

  async updateUser(userId: number | string, updates: { name?: string; email?: string; role?: string; department?: string; departments?: string[]; access_level?: string; status?: string; web_search_enabled?: boolean; password?: string }) {
    const { data } = await this.client.put(`/admin/users/${userId}`, updates)
    return data
  }

  async getDepartments(): Promise<string[]> {
    const { data } = await this.client.get('/admin/departments')
    return (data?.departments as string[]) || []
  }

  async updateDepartments(departments: string[]): Promise<void> {
    await this.client.put('/admin/departments', { departments })
  }

  async createUser(userData: { name?: string; email?: string; role?: string; department?: string; departments?: string[]; access_level?: string; password?: string }) {
    const { data } = await this.client.post('/admin/users', userData)
    return data
  }

  async deleteUser(userId: number | string) {
    const { data } = await this.client.delete(`/admin/users/${userId}`)
    return data
  }

  async unblockUser(userId: number | string) {
    const { data } = await this.client.post(`/admin/users/${userId}/unblock`)
    return data
  }

  // Feedback endpoints
  async submitFeedback(query: string, rating: number, sessionId?: string, assistantAnswer?: string) {
    return this.post('/feedback', { query, rating, session_id: sessionId, assistant_answer: assistantAnswer })
  }

  async getNegativeFeedback(page = 1, perPage = 20) {
    const { data } = await this.client.get('/analytics/negative-feedback', { params: { page, per_page: perPage } })
    return data
  }

  async getSystemStatus() {
    const { data } = await this.client.get('/admin/status')
    return data
  }

  async clearCache() {
    const { data } = await this.client.post('/admin/cache/clear')
    return data
  }

  async getCacheStats() {
    const { data } = await this.client.get('/admin/cache/stats')
    return data
  }

  // Document endpoints
  async getDocuments(
    page = 1,
    perPage = 20,
    params?: { search?: string; access_level?: string; category?: string; department?: string },
  ) {
    const { data } = await this.client.get('/documents', { params: { page, per_page: perPage, ...params } })
    return data
  }

  async getDocumentCategories(): Promise<{ categories: string[]; departments: string[] }> {
    const { data } = await this.client.get('/documents/categories')
    return {
      categories: (data?.categories as string[]) || [],
      departments: (data?.departments as string[]) || [],
    }
  }

  /** Фильтры списка документов по категории и отделу принимает backend;
   *  значения для селектов — из getDocumentCategories(). */

  async updateDocumentAccess(docId: string, accessLevel: string) {
    const { data } = await this.client.put(`/documents/${docId}/access`, {
      access_level: accessLevel,
    })
    return data
  }

  async uploadDocument(file: File, metadata?: { name?: string; category?: string; access_level?: string; department?: string; schedule_delay_seconds?: number }, onProgress?: (pct: number) => void, force = false) {
    const formData = new FormData()
    formData.append('file', file)
    if (metadata?.name) formData.append('name', metadata.name)
    if (metadata?.category) formData.append('category', metadata.category)
    if (metadata?.access_level) formData.append('access_level', metadata.access_level)
    if (metadata?.department) formData.append('department', metadata.department)
    if (metadata?.schedule_delay_seconds) formData.append('schedule_delay_seconds', String(metadata.schedule_delay_seconds))
    try {
      // force=true приходит как query-параметр: backend читает request.query.get("force")
      const { data } = await this.client.post(`/documents/upload${force ? '?force=true' : ''}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'X-Silent-Error': 'true' },
        timeout: 120000,
        // Честный determinate-прогресс отправки тела запроса (XHR upload events).
        // Серверная обработка (process_document -> embeddings -> Chroma) идёт
        // ПОСЛЕ 100% отправки в том же POST — её покрывает indeterminate-фаза в UI.
        onUploadProgress: onProgress
          ? (e: import('axios').AxiosProgressEvent) => {
              const total = e.total ?? file.size ?? 0
              if (total > 0) onProgress(Math.min(100, Math.round((e.loaded / total) * 100)))
            }
          : undefined,
      })
      if ((data as { error?: string })?.error) {
        // backend вернул error в 200 — прокидываем как Error (на случай не-400 ветки)
        const err = new Error((data as { error: string }).error) as Error & { errorCode?: string; status?: number }
        if ((data as { error_code?: string }).error_code) err.errorCode = (data as { error_code: string }).error_code
        throw err
      }
      return data
    } catch (e: unknown) {
      // AxiosError уже обогащён handleError/silent — прокидываем detail без потери
      if (e instanceof Error) throw e
      const axiosErr = e as AxiosError<{ error?: string; message?: string; error_code?: string }>
      const respData = axiosErr.response?.data
      const msg = respData?.error || respData?.message || (e as Error)?.message || 'Ошибка загрузки документа'
      const err = new Error(msg) as Error & { errorCode?: string; status?: number }
      if (respData?.error_code) err.errorCode = respData.error_code
      if (axiosErr.response?.status) err.status = axiosErr.response.status
      throw err
    }
  }

  async getScheduledDocIndexes() {
    const { data } = await this.client.get('/documents/scheduled-indexes')
    return data
  }

  async deleteDocument(docId: string) {
    const { data } = await this.client.delete(`/documents/${docId}`)
    return data
  }

  async deleteAllDocuments() {
    const { data } = await this.client.delete('/documents', { data: { confirm: 'DELETE_ALL' } })
    return data
  }

  async updateDocumentMetadata(docId: string, metadata: { name?: string; category?: string; access_level?: string; department?: string }) {
    const { data } = await this.client.put(`/documents/${docId}/metadata`, metadata)
    return data
  }

  // Suggestion endpoints
  async createSuggestion(text: string, source: 'fab' | 'feedback' | 'profile' | 'mobile' = 'fab') {
    const { data } = await this.client.post('/suggestions', { text, source })
    return data
  }

  async getMySuggestions() {
    const { data } = await this.client.get('/suggestions/mine')
    return data as { items: SuggestionItem[]; total: number }
  }

  async getAdminSuggestions(page = 1, perPage = 20, status = 'pending') {
    const { data } = await this.client.get('/admin/suggestions', { params: { page, per_page: perPage, status } })
    return data
  }

  async updateSuggestion(id: string, updates: { status: string; admin_comment?: string }) {
    const { data } = await this.client.patch(`/admin/suggestions/${id}`, updates)
    return data
  }

  // Chat history endpoints
  async getChatHistory() {
    const { data } = await this.client.get('/chat/history')
    return data
  }

  async saveChatHistory(chat: any) {
    const { data } = await this.client.post('/chat/history', { chat })
    return data
  }

  async deleteChatHistory(id: string) {
    const { data } = await this.client.delete(`/chat/history/${id}`)
    return data
  }

  // Admin: user chats
  async getAdminUserChats() {
    const { data } = await this.client.get('/admin/chats')
    return data
  }

  async getAdminUserChatDetail(userId: number) {
    const { data } = await this.client.get(`/admin/chats/${userId}`)
    return data
  }

  // Custom buttons
  async getChatButtons() {
    const { data } = await this.client.get('/chat/buttons')
    return data
  }

  async getAdminButtons() {
    const { data } = await this.client.get('/admin/buttons')
    return data
  }

  async createAdminButton(btn: { label: string; prompt: string; icon?: string; order?: number; active?: boolean }) {
    const { data } = await this.client.post('/admin/buttons', btn)
    return data
  }

  async updateAdminButton(id: string, btn: any) {
    const { data } = await this.client.put(`/admin/buttons/${id}`, btn)
    return data
  }

  async deleteAdminButton(id: string) {
    const { data } = await this.client.delete(`/admin/buttons/${id}`)
    return data
  }

  // FAQ
  // Основной листинг PG-записей: GET /api/faq -> { items: FaqEntry[] }
  async getFaqEntries() {
    const { data } = await this.client.get('/faq')
    return data as { items: FaqEntry[] }
  }

  // Back-compat алиас: раньше основной список ходил через getFaq()
  async getFaq() {
    return this.getFaqEntries()
  }

  async markFaqViewed(id: number) {
    const { data } = await this.client.post(`/faq/${id}/view`)
    return data
  }

  // Популярные (топ Redis-запросов): GET /api/faq/popular
  async getFaqPopular() {
    const { data } = await this.client.get('/faq/popular')
    return data as { items: { question: string; answer: string }[] }
  }

  // Топ вопросов пользователей с ответами: GET /api/faq/top?page&per_page&search&days
  async getFaqTop(params: FaqTopParams = {}) {
    const { data } = await this.client.get('/faq/top', {
      params,
      headers: { 'X-Silent-Error': 'true' },
    })
    return data as FaqTopResponse
  }

  // Лента объявлений (только мои — фильтр на сервере):
  // GET /api/announcements?page&per_page -> AnnouncementsResponse
  async getAnnouncements(page = 1, per_page = 20) {
    const { data } = await this.client.get('/announcements', {
      params: { page, per_page },
      headers: { 'X-Silent-Error': 'true' },
    })
    return data as AnnouncementsResponse
  }

  // POST /api/announcements — admin/manager. Клиентская пре-валидация
  // зеркалит бэкенд (пустые/длинные/general) — понятные тексты до запроса.
  // X-Silent-Error: тост показывает вызывающий код (успех/сброс/refresh там же),
  // чтобы не было двойного тоста из handleError.
  async createAnnouncement(payload: CreateAnnouncementPayload) {
    const title = String(payload.title ?? '').trim()
    const body = String(payload.body ?? '').trim()
    if (!title) throw new Error('Введите заголовок объявления')
    if (!body) throw new Error('Введите текст объявления')
    if (title.length > ANNOUNCEMENT_TITLE_MAX) {
      throw new Error(`Заголовок слишком длинный (максимум ${ANNOUNCEMENT_TITLE_MAX} символов)`)
    }
    if (body.length > ANNOUNCEMENT_BODY_MAX) {
      throw new Error(`Текст слишком длинный (максимум ${ANNOUNCEMENT_BODY_MAX} символов)`)
    }
    const rawRoles = payload.roles ?? []
    if (!Array.isArray(rawRoles)) throw new Error('Некорректный список ролей')
    const roles = rawRoles.map((r) => String(r ?? '').trim().toLowerCase()).filter(Boolean)
    const badRole = roles.find((r) => !(ANNOUNCEMENT_ROLES as readonly string[]).includes(r))
    if (badRole) {
      throw new Error(`Недопустимая роль: ${badRole} (доступны: ${ANNOUNCEMENT_ROLES.join(', ')})`)
    }
    const rawDepts = payload.departments ?? []
    if (!Array.isArray(rawDepts)) throw new Error('Некорректный список отделов')
    const seen = new Set<string>()
    const departments: string[] = []
    for (const d of rawDepts) {
      const name = String(d ?? '').trim()
      if (!name || seen.has(name.toLowerCase())) continue
      seen.add(name.toLowerCase())
      departments.push(name)
    }
    if (departments.some((d) => d.toLowerCase() === 'general')) {
      throw new Error("Отдел 'general' запрещён — для всех используйте пустой список")
    }
    if (departments.length > ANNOUNCEMENT_DEPARTMENTS_MAX) {
      throw new Error(`Слишком много отделов (максимум ${ANNOUNCEMENT_DEPARTMENTS_MAX})`)
    }
    const longDept = departments.find((d) => d.length > ANNOUNCEMENT_DEPARTMENT_MAX_LEN)
    if (longDept) {
      throw new Error(`Название отдела слишком длинное (максимум ${ANNOUNCEMENT_DEPARTMENT_MAX_LEN} символов): ${longDept.slice(0, 40)}`)
    }
    const { data } = await this.client.post('/announcements', { title, body, roles, departments }, {
      headers: { 'X-Silent-Error': 'true' },
    })
    return data as CreateAnnouncementResponse
  }

  // Back-compat алиас для старого имени
  async getPopularFaq() {
    return this.getFaqPopular()
  }

  async rateFaq(id: number, helpful: boolean) {
    const { data } = await this.client.post(`/faq/${id}/helpful`, { helpful })
    return data
  }

  async getAdminFaq() {
    const { data } = await this.client.get('/admin/faq')
    return data as { faqs: FaqEntry[] }
  }

  async createAdminFaq(item: { question: string; answer: string; category?: string; min_role?: string; variants?: string[] }) {
    const { data } = await this.client.post('/admin/faq', item)
    return data
  }

  async updateAdminFaq(id: string, item: Partial<FaqEntry>) {
    const { data } = await this.client.put(`/admin/faq/${id}`, item)
    return data
  }

  async deleteAdminFaq(id: string) {
    const { data } = await this.client.delete(`/admin/faq/${id}`)
    return data
  }

  // Generic request methods (config?.headers['X-Silent-Error']='true' — без тоста)
  async get<T>(url: string, config?: import('axios').AxiosRequestConfig): Promise<T> {
    const { data } = await this.client.get(url, config)
    return data
  }

  async post<T>(url: string, payload?: unknown, config?: import('axios').AxiosRequestConfig): Promise<T> {
    const { data } = await this.client.post(url, payload, config)
    return data
  }

  async put<T>(url: string, payload?: unknown, config?: import('axios').AxiosRequestConfig): Promise<T> {
    const { data } = await this.client.put(url, payload, config)
    return data
  }

  async delete<T>(url: string, config?: import('axios').AxiosRequestConfig): Promise<T> {
    const { data } = await this.client.delete(url, config)
    return data
  }

  // Widget token methods
  async getWidgetTokens() {
    const { data } = await this.client.get('/admin/widget-tokens')
    return data
  }

  async createWidgetToken(fields: { name: string; title: string; color: string; greeting: string; allowed_domains?: string[] }) {
    const { data } = await this.client.post('/admin/widget-tokens', fields)
    return data
  }

  async updateWidgetToken(id: string, fields: any) {
    const { data } = await this.client.put(`/admin/widget-tokens/${id}`, fields)
    return data
  }

  async deleteWidgetToken(id: string) {
    const { data } = await this.client.delete(`/admin/widget-tokens/${id}`)
    return data
  }

  // Escalation endpoints
  async getEscalations(params?: { status?: string; limit?: number; offset?: number; assigned_to?: string; search?: string }) {
    const { data } = await this.client.get('/admin/escalations', { params })
    return data
  }

  async getUser(userId: string) {
    const { data } = await this.client.get(`/admin/users/${userId}`)
    return data
  }

  async getEscalationDetail(ticketId: string) {
    const { data } = await this.client.get(`/admin/escalations/${ticketId}`)
    return data
  }

  async updateEscalation(ticketId: string, updates: { status?: string; resolved_by?: string; admin_notes?: string; assigned_to?: string; priority?: string }) {
    const { data } = await this.client.patch(`/admin/escalations/${ticketId}`, updates)
    return data
  }

  async getEscalationStats() {
    const { data } = await this.client.get('/admin/escalations/stats')
    return data
  }

  async assignEscalation(ticketId: string, adminId: string) {
    const { data } = await this.client.post(`/admin/escalations/${ticketId}/assign`, { admin_id: adminId })
    return data
  }

  async transferEscalation(ticketId: string, fromAdmin: string, toAdmin: string, reason?: string) {
    const { data } = await this.client.post(`/admin/escalations/${ticketId}/transfer`, { from_admin: fromAdmin, to_admin: toAdmin, reason })
    return data
  }

  async addEscalationComment(ticketId: string, adminId: string, adminName: string, text: string) {
    const { data } = await this.client.post(`/admin/escalations/${ticketId}/comment`, { admin_id: adminId, admin_name: adminName, text })
    return data
  }

  async reopenEscalation(ticketId: string, reason?: string) {
    const { data } = await this.client.post(`/admin/escalations/${ticketId}/reopen`, { reason })
    return data
  }

  async replyToUser(ticketId: string, adminId: string, adminName: string, text: string) {
    const { data } = await this.client.post(`/admin/escalations/${ticketId}/reply`, { admin_id: adminId, admin_name: adminName, text })
    return data
  }

  // ─── Провайдеры LLM: ключи и активная модель (только админ) ───

    /**
   * Сохранённые ключи провайдеров.
   *
   * `shared` — карта «провайдер → id, под которым лежит его ключ».
   * Нужна потому, что у GigaChat отдельный пресет на каждую роль, а учётка
   * одна: ключ сохранён под gigachat_chat, и для gigachat_memory его надо
   * искать там же. Без этой карты предупреждение «Ключ не задан» было
   * ложным.
   */
  async getProviderCredentials() {
    const { data } = await this.client.get('/admin/models/credentials')
    return data as ProviderCredentialsResponse
  }

  async setProviderCredential(providerId: string, credentials: string, scope?: string | null) {
    const { data } = await this.client.put('/admin/models/credentials', {
      provider_id: providerId,
      credentials,
      scope: scope ?? null,
    })
    return data
  }

  async deleteProviderCredential(providerId: string) {
    const { data } = await this.client.delete(`/admin/models/credentials/${providerId}`)
    return data
  }

  async testProviderCredential(providerId: string, credentials?: string) {
    const { data } = await this.client.post('/admin/models/credentials/test', {
      provider_id: providerId,
      credentials: credentials || undefined,
    })
    return data
  }

  async getProviderBackups(providerId: string) {
    const { data } = await this.client.get(`/admin/models/credentials/${providerId}/backups`)
    return data
  }

  async restoreProviderBackup(providerId: string, backupId: number) {
    const { data } = await this.client.post(`/admin/models/credentials/${providerId}/restore`, {
      backup_id: backupId,
    })
    return data
  }

  async getActiveModels() {
    const { data } = await this.client.get('/admin/models/active')
    return data
  }

  /**
   * Что реально установлено на локальном сервере Ollama.
   * reachable: false — сервис не отвечает (штатный случай стенда), это
   * не ошибка, поэтому такие ответы НЕ бросают исключение наружу.
   */
  async getLocalModels() {
    const { data } = await this.client.get('/admin/models/local')
    return data as {
      reachable: boolean
      url: string
      detail: string
      models: { name: string; size: number | null; active_in: string[] }[]
    }
  }

  async setActiveModel(role: string, providerId: string, model: string) {
    const { data } = await this.client.put('/admin/models/active', {
      role,
      provider_id: providerId,
      model,
    })
    return data
  }

  async getReindexDiff() {
    const { data } = await this.client.post('/documents/reindex/diff')
    return data
  }

  // Сверка диска с индексом: что из загруженного не проиндексировано.
  // Отдельная от getReindexDiff — та сравнивает диск с манифестом.
  async getKnowledgeReconcile() {
    const { data } = await this.client.get('/admin/knowledge/reconcile')
    return data
  }

  async scheduleReindex(delaySeconds: number, diff?: any, scheduleAt?: number) {
    const payload: any = { schedule_delay_seconds: delaySeconds, diff }
    if (scheduleAt) payload.schedule_at = scheduleAt
    const { data } = await this.client.post('/documents/rebuild', payload)
    return data
  }

  async cancelReindex() {
    const { data } = await this.client.post('/documents/reindex/cancel')
    return data
  }

  async getOcrStatus() {
    const { data } = await this.client.get('/documents/ocr/status')
    return data
  }

  async getSystemMetrics() {
    const { data } = await this.client.get('/admin/system/metrics')
    return data
  }

  async getOcrPending() {
    const { data } = await this.client.get('/documents/ocr/pending')
    return data
  }

  async decideOcr(payload: { file_id: string; action: 'recognize' | 'skip' }) {
    const { data } = await this.client.post('/documents/ocr/decide', payload)
    return data
  }

  async cancelOcr() {
    const { data } = await this.client.post('/documents/ocr/cancel')
    return data
  }

  async downloadDocument(id: string): Promise<Blob> {
    const { data } = await this.client.get(`/documents/download/${id}`, { responseType: 'blob' })
    return data
  }

  async getPendingReply() {
    const { data } = await this.client.get('/user/pending-reply')
    return data
  }

  // Broadcast endpoints (admin/manager -> users via Telegram bot)
  async getBroadcastRecipients() {
    const { data } = await this.client.get('/broadcast/recipients')
    return data
  }

  async sendBroadcast(userId: number, message: string) {
    const { data } = await this.client.post('/broadcast/send', { user_id: userId, message })
    return data
  }

  async sendBroadcastAll(message: string, userIds?: number[]) {
    const { data } = await this.client.post('/broadcast/send_all', { message, user_ids: userIds })
    return data
  }

  async getBroadcastHistory() {
    const { data } = await this.client.get('/broadcast/history')
    return data
  }

  async aiAnalyticsQuery(question: string) {
    const { data } = await this.client.post('/admin/ai-analytics/query', { question })
    return data
  }

  async aiAnalyticsExport(queryId: string): Promise<Blob> {
    const { data } = await this.client.post('/admin/ai-analytics/export', { query_id: queryId }, { responseType: 'blob' })
    return data
  }
}

export const api = new ApiService()
export default api
